/**
 * Code 128, drawn here rather than fetched.
 *
 * A stores counter needs barcodes on the bins, and the barcodes have to work
 * on an airport network with no internet at all — so this encodes them in the
 * browser, the same way the authenticator's QR is drawn rather than requested
 * from a service.
 *
 * ── why Code 128 and not a QR ───────────────────────────────────────────
 *
 * A QR code needs an imaging scanner or a phone camera. The scanner already
 * sitting on most stores counters is a cheap laser handset that reads ONE
 * dimensional symbols and nothing else, and telling an airport its existing
 * hardware is unsupported is not an answer. Code 128 subset B carries the
 * letters, digits and hyphens a catalogue number is made of — EL-1001,
 * 4500012345, ZZ-7001 — and every scanner made in forty years reads it.
 *
 * A QR is still offered beside it on the label for a phone.
 *
 * ── what a scanner actually is ──────────────────────────────────────────
 *
 * Nearly every handheld scanner is a KEYBOARD. It reads the symbol, types the
 * characters it found and presses Enter. That is why the counter screen needs
 * no driver, no pairing and no permission: a focused text box receives a scan
 * exactly as it receives typing, which is also why the same screen works for
 * somebody with no scanner at all.
 */

/**
 * The 107 bar/space width patterns, by value, then the stop pattern.
 *
 * Each string is a run of widths in modules, starting with a bar: "212222" is
 * a 2-module bar, a 1-module space, a 2-module bar, and so on. The stop
 * pattern is seven runs rather than six because it carries the termination
 * bar. These are the values the standard defines; nothing here is a choice.
 */
const PATTERNS = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
  "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
  "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
  "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
  "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
  "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
  "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
  "114131", "311141", "411131", "211412", "211214", "211232", "2331112",
];

/** Subset B: start character, and the stop. */
const START_B = 104;
const STOP = 106;

/** Everything Code 128 subset B can carry: printable ASCII, space to ~. */
export function encodable(text: string): boolean {
  return /^[\x20-\x7E]+$/.test(text);
}

export interface Barcode {
  /** the text as encoded, which is what a scanner will type */
  text: string;
  /** widths in modules, alternating bar, space, bar, … starting with a bar */
  runs: number[];
  /** total width in modules, quiet zones excluded */
  modules: number;
  /** the check character the symbol carries */
  check: number;
}

/**
 * Encode as Code 128 subset B.
 *
 * The check character is a weighted sum the standard defines: the start value,
 * plus each data value multiplied by its position, modulo 103. A scanner
 * verifies it and stays silent when it fails, which is the whole point — a
 * misread bin label that produced a plausible wrong part number would take
 * the wrong part to an aircraft.
 */
export function code128(text: string): Barcode | null {
  if (!text || !encodable(text)) return null;
  const values = [...text].map((c) => c.charCodeAt(0) - 32);
  let sum = START_B;
  values.forEach((v, i) => {
    sum += v * (i + 1);
  });
  const check = sum % 103;
  const symbols = [START_B, ...values, check, STOP];
  const runs: number[] = [];
  for (const symbol of symbols) {
    for (const width of PATTERNS[symbol]) runs.push(Number(width));
  }
  return { text, runs, modules: runs.reduce((a, b) => a + b, 0), check };
}

/**
 * The bars as rectangles, for an SVG of a given width.
 *
 * Returned as plain numbers rather than markup so the caller owns the drawing
 * — and so this stays testable without a DOM.
 */
export function bars(code: Barcode, width: number, height: number): { x: number; w: number; h: number }[] {
  const unit = width / code.modules;
  const out: { x: number; w: number; h: number }[] = [];
  let x = 0;
  code.runs.forEach((run, i) => {
    /* runs alternate bar, space, bar, … and the first is always a bar */
    if (i % 2 === 0) out.push({ x, w: run * unit, h: height });
    x += run * unit;
  });
  return out;
}
